import Tilt from 'react-parallax-tilt';
import { useInViewAnimation } from './useInViewAnimation';
import { FaCogs, FaInfoCircle, FaPhoneAlt } from 'react-icons/fa';

export default function LogoIntro() {
  const companyLogos = [
    "/ATIM.jpg", "/BLUM_LOGO.png", "/HAWA_LOGO.png", "/HETTICH_LOGO.png",
    "/POZZOLI.png", "/SALICE_LOGO.png", "/STV_LOGO_2.jpg",
    "/SUGATSUNE.jpg", "/VILLES_2000_LOGO.jpg", "/Benzoville.jpg", "/Citterio-Logo.jpg", "/cosmov-Logo.jpg",
    "/ELMES.jpg", "/Ferretto-logo.jpg", "/Furnipart logo.jpg", "/lineacali-logo.jpg", "/Mestre_logo.jpg", "/OLIVARI-LOGO.jpg",
    "/Salice-paolo-logo.jpg", "/Siro logo.jpg"
  ];

  const { ref: statsRef, inView: statsVisible } = useInViewAnimation();
  const { ref: imagesRef, inView: imagesVisible } = useInViewAnimation();
  const { ref: logosRef, inView: logosVisible } = useInViewAnimation();

  const { ref, inView } = useInViewAnimation();

  const iconBaseStyle = "p-4 rounded-full shadow-xl backdrop-blur-sm bg-white/90 text-gray-800 transition-transform transform hover:scale-110 active:scale-95 cursor-pointer";


  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) contactSection.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="min-h-screen bg-white text-gray-900 relative overflow-hidden flex flex-col items-center justify-center px-6 md:px-12 font-poppins">

      {/* Floating Contact Icons */}
      <div
      ref={ref}
      className={`fixed z-40 bottom-10 right-8 flex flex-col items-end space-y-5 transition-all duration-700 ease-out  ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* Product Section Icon */}
      <div
        className={`${iconBaseStyle} animate-wiggle`}
        onClick={() => scrollTo("products")}
        title="Explore Products"
      >
        <FaCogs className="text-base" />
      </div>

      {/* About Us Icon */}
      <div
        className={`${iconBaseStyle} animate-bob`}
        onClick={() => scrollTo("about")}
        title="About Us"
      >
        <FaInfoCircle className="text-base" />
      </div>

      {/* Contact Icon */}
      <div
        className={`${iconBaseStyle} animate-tilt-slow`}
        onClick={() => scrollTo("contact")}
        title="Contact"
      >
        <FaPhoneAlt className="text-base" />
      </div>
    </div>


      {/* Blobs */}
      <div className="absolute -top-32 -left-32 w-[400px] h-[400px] bg-pink-400 opacity-50 rounded-full blur-3xl animate-blob1 mix-blend-multiply" />
      <div className="absolute top-[25%] -left-24 w-[260px] h-[260px] bg-yellow-300 opacity-30 rounded-full blur-2xl animate-blob2 mix-blend-multiply" />
      <div className="absolute bottom-[20%] left-[20%] w-[300px] h-[300px] bg-blue-200 opacity-40 rounded-full blur-2xl animate-blob1 delay-[500ms] mix-blend-multiply" />
      <div className="absolute top-[10%] right-[-80px] w-[300px] h-[300px] bg-purple-300 opacity-40 rounded-full blur-3xl animate-blob2 delay-[1000ms] mix-blend-multiply" />
      <div className="absolute bottom-[-60px] right-[25%] w-[280px] h-[280px] bg-green-300 opacity-40 rounded-full blur-3xl animate-blob1 delay-[1500ms] mix-blend-multiply" />

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl w-full flex-1 flex flex-col md:flex-row items-center justify-between space-y-12 md:space-y-0 md:space-x-16 pt-12">

        {/* Text and Stats */}
        <div
          ref={statsRef}
          className={`flex-1 text-center md:text-left space-y-6 transition-all duration-700 ease-out ${
            statsVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
          }`}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
            Nishar Hardware
          </h1>
          <p className="text-lg md:text-xl text-gray-600 font-light italic leading-relaxed">
            Trusted Since 1987 — Quality. Affordability. Reliability.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-6">
            {[
              { value: "2500+", label: "Clients" },
              { value: "1000+", label: "Products" },
              { value: "3421+", label: "Services" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-white via-gray-50 to-gray-200/60 p-5 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-transform hover:scale-105 animate-float"
              >
                <h2 className="text-2xl font-semibold text-gray-800">{item.value}</h2>
                <p className="text-xs text-gray-500 font-medium">{item.label}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={scrollToContact}
            className="mt-6 inline-block px-6 py-3 bg-black text-white rounded-full shadow hover:bg-gray-800 transition duration-300"
          >
            Get in Touch →
          </button>
        </div>

        {/* Product Images */}
        <div
          ref={imagesRef}
          className={`flex-1 grid grid-cols-2 gap-4 transition-all duration-700 ${
            imagesVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
          }`}
        >
          {[
            "/RADIAL-1.jpg",
            "/walking wardrobe.jpg",
            "/INVISIBLE SLIDING.jpg",
            "/CLUB-1.jpg"
          ].map((src, i) => (
            <Tilt key={i} tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.03} className="animate-float delay-[100ms*i]">
              <div className="relative overflow-hidden rounded-xl shadow-xl group border-4 border-gray-100 bg-white p-1">
                <img
                  src={src}
                  alt={`Product ${i + 1}`}
                  className="h-40 md:h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </Tilt>
          ))}
        </div>
      </div>

      {/* Company Logos */}
      <div
        ref={logosRef}
        className={`w-full overflow-hidden mt-12 border-t border-gray-200 pt-4 pb-4 transition-all duration-1000 ${
          logosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <h2 className="text-xl font-semibold text-gray-800 text-center mb-4">Our Trusted Partners</h2>
        <div className="flex animate-marquee whitespace-nowrap gap-10">
          {[...companyLogos, ...companyLogos].map((logo, index) => (
            <img
              key={index}
              src={logo}
              alt={`Logo ${index + 1}`}
              className="h-12 md:h-16 w-auto object-contain grayscale hover:grayscale-0 transition duration-300"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
